import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { OnChangeValue, ActionMeta, GroupBase } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Domain } from '@tiendanube/common/src/enums';
import {
  multiSelectColourStyles,
  multiSelectColourStylesGrouped,
} from './multiSelectStyleConfig';
import { OptionType } from '../PredictMultiSelect/styleConfig';

interface MultiSelectProps {
  defaultValue: OptionType[] | undefined;
  options: OptionType[] | GroupBase<OptionType>[];
  placeholder: string;
  onChange: (
    newValue: OnChangeValue<OptionType, true>,
    action: ActionMeta<OptionType>,
  ) => void;
  inputId?: string;
}

function MultiSelect({
  defaultValue,
  options,
  placeholder,
  onChange,
  inputId,
}: Readonly<MultiSelectProps>) {
  const { t } = useTranslation([Domain.ORDERS]);
  const [inputValue, setInputValue] = useState('');

  const hasGroups = options.some((opt) => 'options' in opt);
  const styles = hasGroups
    ? multiSelectColourStylesGrouped
    : multiSelectColourStyles;

  // Calculate available options (excluding already selected ones)
  const selectedValues = useMemo(
    () => defaultValue?.map(({ value }) => value) || [],
    [defaultValue],
  );

  const availableOptions = useMemo(() => {
    if (hasGroups) {
      return (options as GroupBase<OptionType>[]).some((group) =>
        group.options.some((opt) => !selectedValues.includes(opt.value)),
      );
    }
    return (options as OptionType[]).some(
      (opt) => !selectedValues.includes(opt.value),
    );
  }, [hasGroups, options, selectedValues]);

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
  };

  const noOptionsMessage = useCallback(() => {
    if (inputValue.trim() !== '') {
      return t('filter.noResultsFound');
    }
    return null;
  }, [inputValue, t]);

  return (
    <CreatableSelect
      isMulti
      options={options}
      value={defaultValue}
      styles={styles}
      placeholder={placeholder}
      onChange={onChange}
      inputId={inputId}
      onInputChange={handleInputChange}
      isValidNewOption={() => false}
      noOptionsMessage={noOptionsMessage}
      menuIsOpen={
        availableOptions || inputValue.trim() !== '' ? undefined : false
      }
    />
  );
}
export default MultiSelect;
